import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, className = "", y = 28 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.75, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const MaskedLines = ({ lines, as: Tag = "h1", className = "", lineClass = "", baseDelay = 0.1 }) => (
  <Tag className={className}>
    {lines.map((l, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
          className={`block ${lineClass}`}
          initial={{ y: "115%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: baseDelay + i * 0.13, ease: EASE }}
        >
          {l}
        </motion.span>
      </span>
    ))}
  </Tag>
);

export const Marquee = ({ items, className = "" }) => (
  <div className={`overflow-hidden border-y border-line bg-surface ${className}`} data-testid="editorial-marquee">
    <div className="flex w-max animate-marquee whitespace-nowrap py-4">
      {[0, 1].map((n) => (
        <div key={n} className="flex items-center">
          {items.map((t, i) => (
            <span key={i} className="flex items-center">
              <span className="px-8 font-display text-sm sm:text-base font-semibold uppercase tracking-[0.3em] text-neutral-500">{t}</span>
              <span className="h-1.5 w-1.5 rotate-45 bg-regal-amber/70" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const SectionHead = ({ overline, title, copy, align = "left", as: HeadingTag = "h2", children }) => (
  <div className={`mb-12 sm:mb-16 ${align === "center" ? "text-center mx-auto max-w-3xl" : "max-w-3xl"}`}>
    <Reveal><p className="overline-label mb-4">{overline}</p></Reveal>
    <Reveal delay={0.08}>
      <HeadingTag className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-[1.08]">{title}</HeadingTag>
    </Reveal>
    {copy && <Reveal delay={0.16}><p className="mt-5 text-base sm:text-lg text-neutral-400 leading-relaxed">{copy}</p></Reveal>}
    {children}
  </div>
);

export const CountUp = ({ to, suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  // Initial state is the FINAL value so server-rendered/prerendered HTML always
  // contains the real figure (20+ / 1,000+ / 4WD) — the count-up is a visual
  // enhancement for live viewers only, never required for the value to exist.
  const [val, setVal] = useState(to);
  useEffect(() => {
    if (!inView) return;
    setVal(0);
    const controls = animate(0, to, { duration: 1.8, ease: EASE, onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref} className={className}>{val.toLocaleString("en-GB")}{suffix}</span>;
};

export const PortalButton = ({ label = "FILE PORTAL", className = "", testId = "portal-button" }) => (
  <a
    href="https://portal.regaltuning.com"
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 bg-regal-amber text-void font-display font-bold uppercase tracking-[0.14em] text-sm px-6 py-3.5 hover:bg-amber-400 transition-colors duration-300 ${className}`}
  >
    {label}
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M7 17L17 7M7 7h10v10" /></svg>
  </a>
);
